import React, { useState, useRef, useEffect } from "react";
import { Globe, Gauge, Brain, ChevronDown, Lock } from "lucide-react";

const LANGUAGES = ["Auto", "English", "Italiano", "Español", "Français", "Deutsch", "Português", "中文", "日本語", "हिन्दी", "العربية"];
const REASONING = ["Quick", "Balanced", "Deep", "Critical"];
const ATTITUDE = ["Neutral", "Analytic", "Creative", "Empathetic"];

const Dropdown = ({ icon: Icon, label, value, options, onChange, testid, lockedOptions = [], onLocked }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="flex flex-col items-center gap-2" ref={ref}>
      <div className="flex items-center gap-1.5 text-primary/80">
        <Icon strokeWidth={1.5} className="w-3.5 h-3.5" />
        <span className="text-[10px] tracking-[0.16em] uppercase text-muted-foreground">{label}</span>
      </div>
      <div className="relative">
        <button
          data-testid={testid}
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 bg-secondary border border-border hover:border-primary/40 rounded-full px-3.5 py-1.5 text-sm text-foreground transition-colors min-w-[110px] justify-between"
        >
          {value}
          <ChevronDown strokeWidth={1.5} className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
        {open && (
          <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-40 max-h-64 overflow-auto bg-popover border border-border rounded-lg shadow-xl py-1 z-30 scrollbar-thin">
            {options.map((o) => {
              const locked = lockedOptions.includes(o);
              return (
                <button
                  key={o}
                  data-testid={`${testid}-option-${o}`}
                  onClick={() => {
                    setOpen(false);
                    if (locked) {
                      onLocked?.(o);
                      return;
                    }
                    onChange(o);
                  }}
                  className={`w-full flex items-center justify-between gap-2 text-left px-4 py-2 text-sm hover:bg-secondary transition-colors ${
                    o === value ? "text-primary" : locked ? "text-muted-foreground/70" : "text-foreground"
                  }`}
                >
                  <span>{o}</span>
                  {locked && <Lock strokeWidth={1.6} className="w-3.5 h-3.5 text-primary/70 shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const CognitiveCommand = ({ settings, onChange, entitled = false, onUpgrade }) => (
  <div data-testid="cognitive-command" className="rounded-2xl border border-border bg-secondary/40 px-6 py-5">
    <p className="text-center text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-5">
      Cognitive Command
    </p>
    <div className="flex items-start justify-center gap-6 sm:gap-10 flex-wrap">
      <Dropdown
        icon={Globe}
        label="Language"
        testid="setting-language"
        value={settings.language}
        options={LANGUAGES}
        onChange={(v) => onChange({ ...settings, language: v })}
      />
      <Dropdown
        icon={Gauge}
        label="Reasoning"
        testid="setting-reasoning"
        value={settings.reasoning}
        options={REASONING}
        lockedOptions={entitled ? [] : ["Deep", "Critical"]}
        onLocked={onUpgrade}
        onChange={(v) => onChange({ ...settings, reasoning: v })}
      />
      <Dropdown
        icon={Brain}
        label="Attitude"
        testid="setting-attitude"
        value={settings.attitude}
        options={ATTITUDE}
        onChange={(v) => onChange({ ...settings, attitude: v })}
      />
    </div>
  </div>
);
